import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { ArrowUpRight, X } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Milan center: approx piazza Duomo
const MILANO_CENTER = [45.4685, 9.185];

// OpenStreetMap standard tiles + CSS invert filter → dark editorial look.
// Fully free, no API key required, works anywhere.
const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** Brass-colored SVG pin used as Leaflet DivIcon. */
function makePinIcon(active = false, isOpening = false) {
  const color = active ? "#E4B074" : isOpening ? "#B67E48" : "#C48F56";
  const outerRing = active ? "12" : "9";
  const inner = active ? "5" : "4";
  return L.divIcon({
    className: "ih-pin",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `
      <div style="position:relative;width:28px;height:28px;">
        <div style="
          position:absolute;inset:0;
          border-radius:50%;
          background:${color}22;
          border:1px solid ${color}88;
          animation: ${active ? "ih-pulse 1.4s ease-out infinite" : "none"};
        "></div>
        <div style="
          position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
          width:${outerRing}px;height:${outerRing}px;border-radius:50%;
          background:${color};box-shadow:0 0 0 3px rgba(10,9,8,0.9);
        "></div>
        <div style="
          position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
          width:${inner}px;height:${inner}px;border-radius:50%;
          background:#0A0908;
        "></div>
      </div>
    `,
  });
}

// Inject pulse animation once
function ensurePulseCSS() {
  if (typeof document === "undefined") return;
  if (document.getElementById("ih-pin-css")) return;
  const s = document.createElement("style");
  s.id = "ih-pin-css";
  s.textContent = `
    @keyframes ih-pulse {
      0%   { transform: scale(1);   opacity: .9; }
      70%  { transform: scale(2.2); opacity: 0; }
      100% { transform: scale(2.2); opacity: 0; }
    }
    .ih-pin { background: transparent !important; border: none !important; }
    .leaflet-container { background: #0A0908; font-family: "Instrument Sans", sans-serif; }
    /* Dark editorial look via CSS filter on the OSM standard tiles */
    .leaflet-tile-pane {
      filter: invert(1) hue-rotate(180deg) brightness(0.85) contrast(0.9) saturate(0.6);
    }
    .leaflet-control-attribution {
      background: rgba(10,9,8,0.85) !important;
      color: rgba(255,255,255,0.4) !important;
      font-size: 9px !important;
      padding: 3px 6px !important;
    }
    .leaflet-control-attribution a {
      color: rgba(196,143,86,0.85) !important;
    }
    .leaflet-control-zoom a {
      background: rgba(15,13,11,0.9) !important;
      color: rgba(255,255,255,0.7) !important;
      border-color: rgba(255,255,255,0.1) !important;
    }
    .leaflet-control-zoom a:hover {
      background: rgba(196,143,86,0.2) !important;
      color: #C48F56 !important;
    }
  `;
  document.head.appendChild(s);
}

/** Fit map to the pins' bounds on mount. */
function FitToPins({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(b, { padding: [40, 40], maxZoom: 14 });
  }, [map, points]);
  return null;
}

export default function RigenerazioneMap() {
  const [hotels, setHotels] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [active, setActive] = useState(null); // {kind, data}
  const [mode, setMode] = useState("map"); // 'map' | 'poster'

  useEffect(() => {
    ensurePulseCSS();
    Promise.all([
      axios.get(`${API}/hotels`).catch(() => ({ data: [] })),
      axios.get(`${API}/openings`).catch(() => ({ data: [] })),
    ]).then(([h, o]) => {
      setHotels(h.data);
      setOpenings(o.data);
    });
  }, []);

  const allPoints = useMemo(() => {
    const hs = hotels
      .filter((h) => h.lat && h.lng)
      .map((h) => ({ ...h, kind: "hotel" }));
    const os = openings
      .filter((o) => o.lat && o.lng)
      .map((o) => ({ ...o, kind: "opening" }));
    return [...hs, ...os];
  }, [hotels, openings]);

  return (
    <section
      data-testid="rigenerazione-map"
      className="relative border-t border-white/5 py-20 md:py-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-10 md:mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              Rigenerazione · Milano
            </div>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95] text-balance">
              La geografia{" "}
              <span
                className="font-serif italic text-[#C48F56]"
                style={{ fontWeight: 400 }}
              >
                dei nostri progetti.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-8 flex flex-col justify-end">
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
              Ogni hotel racconta un pezzo di rigenerazione urbana milanese.
              Esplora la mappa per scoprire il nostro portafoglio operativo
              e le prossime aperture — dai grandi assi metropolitani al cuore
              storico della città.
            </p>
            {/* Toggle */}
            <div className="inline-flex rounded-full border border-white/10 p-1 bg-black/40 self-start">
              <button
                onClick={() => setMode("map")}
                data-testid="map-mode-leaflet"
                className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] rounded-full transition-colors ${
                  mode === "map"
                    ? "bg-[#C48F56] text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Mappa esplorabile
              </button>
              <button
                onClick={() => setMode("poster")}
                data-testid="map-mode-poster"
                className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] rounded-full transition-colors ${
                  mode === "poster"
                    ? "bg-[#C48F56] text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Poster editoriale
              </button>
            </div>
          </div>
        </div>

        {/* Map / Poster */}
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0A0908]">
          <AnimatePresence mode="wait">
            {mode === "map" ? (
              <motion.div
                key="leaflet"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full aspect-[16/10] md:aspect-[16/9]"
                data-testid="leaflet-wrap"
              >
                <MapContainer
                  center={MILANO_CENTER}
                  zoom={12}
                  scrollWheelZoom={false}
                  style={{ height: "100%", width: "100%" }}
                  zoomControl={true}
                >
                  <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
                  <FitToPins points={allPoints} />
                  {allPoints.map((p) => (
                    <Marker
                      key={`${p.kind}-${p.slug}`}
                      position={[p.lat, p.lng]}
                      icon={makePinIcon(
                        active?.data?.slug === p.slug,
                        p.kind === "opening"
                      )}
                      eventHandlers={{
                        click: () => setActive({ kind: p.kind, data: p }),
                      }}
                    />
                  ))}
                </MapContainer>
              </motion.div>
            ) : (
              <motion.div
                key="poster"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="poster-wrap"
              >
                <EditorialPoster
                  points={allPoints}
                  active={active}
                  onSelect={(p) => setActive({ kind: p.kind, data: p })}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Detail panel overlay */}
          <AnimatePresence>
            {active && (
              <motion.div
                key="detail"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                transition={{ duration: 0.35 }}
                className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:w-[380px] rounded-xl bg-[#0F0D0B]/95 backdrop-blur border border-white/10 p-5 md:p-6 z-[500]"
                data-testid="map-detail-panel"
              >
                <button
                  onClick={() => setActive(null)}
                  data-testid="map-detail-close"
                  className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#C48F56] mb-2">
                  {active.kind === "hotel" ? "Hotel operativo" : "In apertura"}
                  {" · "}
                  {active.data.area}
                </div>
                <div className="font-display text-2xl leading-tight mb-2">
                  {active.data.name}
                </div>
                {active.data.opening && (
                  <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                    Apertura · {active.data.opening}
                  </div>
                )}
                {active.data.stars > 0 && (
                  <div className="text-xs text-[#C48F56] mb-3">
                    {"★".repeat(active.data.stars)}
                    {active.data.rooms > 0 && (
                      <span className="text-white/50 ml-3">
                        {active.data.rooms} camere
                      </span>
                    )}
                  </div>
                )}
                <p className="text-white/65 text-sm leading-relaxed line-clamp-4 mb-4">
                  {active.data.description || active.data.tagline}
                </p>
                {active.kind === "hotel" ? (
                  <Link
                    to={`/hotel/${active.data.slug}`}
                    data-testid="map-detail-link"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C48F56] hover:text-white transition-colors"
                  >
                    Scopri l'hotel <ArrowUpRight size={13} />
                  </Link>
                ) : (
                  active.data.project_url && (
                    <Link
                      to={active.data.project_url}
                      data-testid="map-detail-link"
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C48F56] hover:text-white transition-colors"
                    >
                      Il progetto <ArrowUpRight size={13} />
                    </Link>
                  )
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          <span className="inline-flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#C48F56]" />
            {hotels.filter((h) => h.lat).length} hotel operativi
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#B67E48] ring-2 ring-[#B67E48]/30" />
            {openings.filter((o) => o.lat).length} nuove aperture
          </span>
          <span className="text-white/30 normal-case tracking-normal">
            · Mappa · OpenStreetMap · Coordinate CMS-editabili
          </span>
        </div>
      </div>
    </section>
  );
}

/* ============================ POSTER EDITORIALE (SVG) ============================ */

/**
 * A stylised, editorial silhouette of Milano's ring roads with pins projected
 * from real lat/lng. Not geographically accurate, but visually cohesive.
 *
 * Bounds tuned to include Rho-Fiera (west) and Bicocca (north-east).
 */
const POSTER_BOUNDS = {
  minLat: 45.44,
  maxLat: 45.545,
  minLng: 9.05,
  maxLng: 9.24,
};
const POSTER_W = 900;
const POSTER_H = 620;

function project(lat, lng) {
  const { minLat, maxLat, minLng, maxLng } = POSTER_BOUNDS;
  const x = ((lng - minLng) / (maxLng - minLng)) * POSTER_W;
  // invert Y (SVG grows down, lat grows up)
  const y = POSTER_H - ((lat - minLat) / (maxLat - minLat)) * POSTER_H;
  return { x, y };
}

function EditorialPoster({ points, active, onSelect }) {
  const svgRef = useRef(null);
  return (
    <div className="relative w-full aspect-[16/10] md:aspect-[16/9] bg-[#0A0908] overflow-hidden">
      {/* Subtle grid backdrop */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${POSTER_W} ${POSTER_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
      >
        {/* Corner brackets */}
        <PosterBracket x={30} y={30} />
        <PosterBracket x={POSTER_W - 30} y={30} rot={90} />
        <PosterBracket x={30} y={POSTER_H - 30} rot={-90} />
        <PosterBracket x={POSTER_W - 30} y={POSTER_H - 30} rot={180} />

        {/* Vertical rule + eyebrow */}
        <line
          x1={70}
          x2={70}
          y1={80}
          y2={POSTER_H - 80}
          stroke="rgba(255,255,255,0.06)"
        />
        <text
          x={70}
          y={70}
          fill="#C48F56"
          fontSize="11"
          fontFamily="JetBrains Mono, monospace"
          letterSpacing="4"
        >
          MILANO · 45.47°N · 9.19°E
        </text>

        {/* Concentric editorial rings around Duomo */}
        {[80, 160, 240, 320].map((r, i) => {
          const c = project(45.4642, 9.19);
          return (
            <circle
              key={i}
              cx={c.x}
              cy={c.y}
              r={r}
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeDasharray="2 4"
            />
          );
        })}

        {/* Ring roads (simplified stylized routes) */}
        <ellipse
          cx={project(45.4642, 9.19).x}
          cy={project(45.4642, 9.19).y}
          rx={180}
          ry={130}
          fill="none"
          stroke="rgba(196,143,86,0.15)"
          strokeWidth="1.4"
        />
        <ellipse
          cx={project(45.4642, 9.19).x}
          cy={project(45.4642, 9.19).y}
          rx={330}
          ry={230}
          fill="none"
          stroke="rgba(196,143,86,0.1)"
          strokeWidth="1"
          strokeDasharray="6 4"
        />

        {/* Landmark: Duomo center */}
        <g>
          <circle
            cx={project(45.4642, 9.19).x}
            cy={project(45.4642, 9.19).y}
            r="4"
            fill="rgba(255,255,255,0.5)"
          />
          <text
            x={project(45.4642, 9.19).x + 10}
            y={project(45.4642, 9.19).y + 4}
            fill="rgba(255,255,255,0.5)"
            fontSize="9"
            fontFamily="JetBrains Mono, monospace"
            letterSpacing="2"
          >
            DUOMO
          </text>
        </g>

        {/* Pins */}
        {(() => {
          // Simple label collision avoidance: sort by y then offset overlapping labels.
          const projected = points.map((p) => ({
            ...p,
            _pos: project(p.lat, p.lng),
          }));
          projected.sort((a, b) => a._pos.y - b._pos.y);
          const MIN_GAP = 14;
          for (let i = 1; i < projected.length; i++) {
            const prev = projected[i - 1];
            const curr = projected[i];
            // Only offset if labels are on the same side and vertically close
            const sameCluster = Math.abs(curr._pos.x - prev._pos.x) < 140;
            if (sameCluster && curr._pos.y - prev._pos.y < MIN_GAP) {
              curr._labelY = prev._labelY !== undefined
                ? prev._labelY + MIN_GAP
                : prev._pos.y + MIN_GAP;
            } else {
              curr._labelY = curr._pos.y;
            }
          }
          projected[0]._labelY = projected[0]._pos.y;

          return projected.map((p) => {
            const { x, y } = p._pos;
            const labelY = p._labelY ?? y;
            const isActive = active?.data?.slug === p.slug;
            const isOpening = p.kind === "opening";
            const color = isActive
              ? "#E4B074"
              : isOpening
              ? "#B67E48"
              : "#C48F56";
            return (
              <g
                key={`${p.kind}-${p.slug}`}
                style={{ cursor: "pointer" }}
                onClick={() => onSelect(p)}
                data-testid={`poster-pin-${p.slug}`}
              >
                {/* Connector line to displaced label */}
                {Math.abs(labelY - y) > 2 && (
                  <line
                    x1={x + 4}
                    y1={y}
                    x2={x + 8}
                    y2={labelY}
                    stroke={color}
                    strokeOpacity="0.3"
                    strokeWidth="0.6"
                  />
                )}
                <g transform={`translate(${x},${y})`}>
                  {isActive && (
                    <circle r="18" fill={color} fillOpacity="0.12" stroke={color} strokeOpacity="0.4">
                      <animate attributeName="r" from="12" to="26" dur="1.6s" repeatCount="indefinite" />
                      <animate attributeName="fill-opacity" from="0.25" to="0" dur="1.6s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle r={isActive ? 6 : 5} fill={color} stroke="#0A0908" strokeWidth="2.5" />
                  {isOpening && !isActive && (
                    <circle r="10" fill="none" stroke={color} strokeOpacity="0.4" />
                  )}
                </g>
                <text
                  x={x + 10}
                  y={labelY + 4}
                  fill={isActive ? "#E4B074" : "rgba(255,255,255,0.85)"}
                  fontSize="10"
                  fontFamily="Instrument Sans, sans-serif"
                  fontWeight={isActive ? 600 : 400}
                >
                  {p.name}
                </text>
              </g>
            );
          });
        })()}
      </svg>
    </div>
  );
}

function PosterBracket({ x, y, rot = 0 }) {
  return (
    <g transform={`translate(${x},${y}) rotate(${rot})`}>
      <line x1={0} y1={0} x2={22} y2={0} stroke="rgba(196,143,86,0.55)" />
      <line x1={0} y1={0} x2={0} y2={22} stroke="rgba(196,143,86,0.55)" />
    </g>
  );
}
